import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';
import {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import {
  UserRole,
  getUserRole,
  initializeUserRole,
  canCreateArticle as checkCanCreateArticle,
  canEditArticle as checkCanEditArticle,
  canDeleteArticle as checkCanDeleteArticle,
  canManageRoles as checkCanManageRoles,
  canCreateQuestionCatalog as checkCanCreateQuestionCatalog,
  canEditQuestionCatalog as checkCanEditQuestionCatalog,
  canDeleteQuestionCatalog as checkCanDeleteQuestionCatalog,
} from '@/services/roleManager';

interface AuthContextType {
  user: ReturnType<typeof useUser>['user'];
  userRole: UserRole;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasProAccess: boolean;
  hasFreeAccess: boolean;
  signOut: () => Promise<void>;
  // Role-based permission methods
  canCreateArticle: () => boolean;
  canEditArticle: () => boolean;
  canDeleteArticle: () => boolean;
  canManageRoles: () => boolean;
  canCreateQuestionCatalog: () => boolean;
  canEditQuestionCatalog: () => boolean;
  canDeleteQuestionCatalog: () => boolean;
  // Role management
  refreshRole: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded, signOut: clerkSignOut } = useClerkAuth();
  const { user } = useUser();
  const [userRole, setUserRole] = useState<UserRole>('READER');
  const [roleLoading, setRoleLoading] = useState(true);

  // Check if user has pro subscription using Clerk billing
  const { has } = useClerkAuth();
  const [hasProAccess, setHasProAccess] = useState(false);
  const [hasFreeAccess, setHasFreeAccess] = useState(false);

  // Update billing access when Clerk is loaded
  useEffect(() => {
    if (isLoaded && has && typeof has === 'function') {
      try {
        const proAccess = has({ plan: 'pro' }) || false;
        const freeAccess =
          has({ plan: 'free_user' }) || (!proAccess && isSignedIn);

        setHasProAccess(proAccess);
        setHasFreeAccess(freeAccess);
      } catch (error) {
        console.warn('Error checking billing plans:', error);
        // Fallback: treat all signed-in users as free users
        setHasProAccess(false);
        setHasFreeAccess(isSignedIn || false);
      }
    } else if (isLoaded) {
      // Fallback when has function is not available
      setHasProAccess(false);
      setHasFreeAccess(isSignedIn || false);
    }
  }, [isLoaded, isSignedIn, has]);

  // Load user role when authenticated
  useEffect(() => {
    const loadUserRole = async () => {
      if (isSignedIn && user?.id && isLoaded) {
        setRoleLoading(true);
        try {
          // Initialize role if this is a new user
          await initializeUserRole(user.id);

          // Get current role
          const role = await getUserRole(user.id);
          setUserRole(role);
        } catch (error) {
          console.error('Error loading user role:', error);
          setUserRole('READER'); // Fallback
        } finally {
          setRoleLoading(false);
        }
      } else if (!isSignedIn) {
        setUserRole('READER');
        setRoleLoading(false);
      }
    };

    loadUserRole();
  }, [isSignedIn, user?.id, isLoaded]);

  // Refresh role function
  const refreshRole = async () => {
    if (user?.id) {
      setRoleLoading(true);
      try {
        const role = await getUserRole(user.id);
        setUserRole(role);
      } catch (error) {
        console.error('Error refreshing user role:', error);
      } finally {
        setRoleLoading(false);
      }
    }
  };

  // Role-based permission methods
  const canCreateArticle = () =>
    Boolean(isSignedIn && checkCanCreateArticle(userRole));
  const canEditArticle = () =>
    Boolean(isSignedIn && checkCanEditArticle(userRole));
  const canDeleteArticle = () =>
    Boolean(isSignedIn && checkCanDeleteArticle(userRole));
  const canManageRoles = () =>
    Boolean(isSignedIn && checkCanManageRoles(userRole));
  const canCreateQuestionCatalog = () =>
    Boolean(isSignedIn && checkCanCreateQuestionCatalog(userRole));
  const canEditQuestionCatalog = () =>
    Boolean(isSignedIn && checkCanEditQuestionCatalog(userRole));
  const canDeleteQuestionCatalog = () =>
    Boolean(isSignedIn && checkCanDeleteQuestionCatalog(userRole));

  const signOut = async () => {
    await clerkSignOut();
    setUserRole('READER');
  };

  const contextValue: AuthContextType = {
    user,
    userRole,
    isAuthenticated: isSignedIn || false,
    isLoading: !isLoaded || roleLoading,
    hasProAccess,
    hasFreeAccess,
    signOut,
    canCreateArticle,
    canEditArticle,
    canDeleteArticle,
    canManageRoles,
    canCreateQuestionCatalog,
    canEditQuestionCatalog,
    canDeleteQuestionCatalog,
    refreshRole,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
